import {
  Box,
  CircularProgress,
  LinearProgress,
  styled,
  Typography,
} from "@mui/material";
import React, { memo } from "react";

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 12px 16px;
  margin-bottom: 1px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled(Typography)`
  font-size: 14px;
  color: #008069;
  font-weight: 500;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "16px",
  backgroundColor: "#f8f9fa",
  borderRadius: "12px",
  border: "1px solid #e9ecef",
});

const StatusIndicator = styled(Box)<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${(props) => {
    switch (props.status) {
      case "sending":
        return "#e3f2fd";
      case "completed":
        return "#e8f5e8";
      case "error":
        return "#ffebee";
      default:
        return "#f5f5f5";
    }
  }};
  border-left: 4px solid
    ${(props) => {
      switch (props.status) {
        case "sending":
          return "#2196f3";
        case "completed":
          return "#4caf50";
        case "error":
          return "#f44336";
        default:
          return "#9e9e9e";
      }
    }};
`;

const StatItem = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const StatLabel = styled(Typography)`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const StatValue = styled(Typography)<{ color?: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.color || "#333"};
`;

interface SendProgress {
  total: number;
  sent: number;
  failed: number;
  current: number;
  status: "idle" | "sending" | "completed" | "error";
}

interface ProgressTrackerProps {
  sendProgress: SendProgress;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = memo(
  ({ sendProgress }) => {
    if (sendProgress.status === "idle") {
      return null;
    }

    const getStatusText = (status: string) => {
      switch (status) {
        case "sending":
          return "Enviando...";
        case "completed":
          return "Concluído";
        case "error":
          return "Erro";
        default:
          return "Aguardando...";
      }
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case "sending":
          return <CircularProgress size={16} />;
        case "completed":
          return (
            <Typography sx={{ color: "#4caf50", fontSize: "16px" }}>
              ✓
            </Typography>
          );
        case "error":
          return (
            <Typography sx={{ color: "#f44336", fontSize: "16px" }}>
              ✗
            </Typography>
          );
        default:
          return null;
      }
    };

    const progressPercentage =
      sendProgress.total > 0
        ? Math.round((sendProgress.sent / sendProgress.total) * 100)
        : 0;

    return (
      <BoxWrapper>
        <SectionTitle
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#111b21" }}
        >
          Progresso do Envio
        </SectionTitle>

        <ProgressContainer>
          {/* Status */}
          <StatusIndicator status={sendProgress.status}>
            {getStatusIcon(sendProgress.status)}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Status: {getStatusText(sendProgress.status)}
            </Typography>
          </StatusIndicator>

          {/* Barra de progresso */}
          {sendProgress.status === "sending" && sendProgress.total > 0 && (
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Progresso
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {progressPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor: "#008069",
                  },
                }}
              />
            </Box>
          )}

          {/* Estatísticas */}
          {sendProgress.total > 0 && (
            <>
              <StatItem>
                <StatLabel>Total de destinatários:</StatLabel>
                <StatValue>{sendProgress.total}</StatValue>
              </StatItem>

              <StatItem>
                <StatLabel>Enviados com sucesso:</StatLabel>
                <StatValue color="#4caf50">{sendProgress.sent}</StatValue>
              </StatItem>

              {sendProgress.failed > 0 && (
                <StatItem>
                  <StatLabel>Falharam:</StatLabel>
                  <StatValue color="#f44336">{sendProgress.failed}</StatValue>
                </StatItem>
              )}

              <StatItem>
                <StatLabel>Taxa de sucesso:</StatLabel>
                <StatValue
                  color={sendProgress.sent > 0 ? "#4caf50" : "#f44336"}
                >
                  {sendProgress.total > 0
                    ? Math.round((sendProgress.sent / sendProgress.total) * 100)
                    : 0}
                  %
                </StatValue>
              </StatItem>
            </>
          )}

          {/* Mensagem de conclusão */}
          {sendProgress.status === "completed" && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#e8f5e8",
                borderRadius: "8px",
                border: "1px solid #4caf50",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#2e7d32", fontWeight: 500 }}
              >
                ✅ Template enviado com sucesso!
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#2e7d32", display: "block", mt: 0.5 }}
              >
                {sendProgress.sent} mensagem(s) enviada(s) de{" "}
                {sendProgress.total} destinatário(s)
              </Typography>
            </Box>
          )}

          {/* Mensagem de erro */}
          {sendProgress.status === "error" && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#ffebee",
                borderRadius: "8px",
                border: "1px solid #f44336",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#c62828", fontWeight: 500 }}
              >
                ❌ Erro no envio do template
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#c62828", display: "block", mt: 0.5 }}
              >
                Verifique os parâmetros e tente novamente
              </Typography>
            </Box>
          )}
        </ProgressContainer>
      </BoxWrapper>
    );
  },
);

ProgressTracker.displayName = "ProgressTracker";

export default ProgressTracker;
